import React, { useState, useRef } from "react";
import {
  Popper,
  Paper,
  Typography,
  Box,
  IconButton,
  ClickAwayListener,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../assets/theme";
import { RateType } from "../services/RoutingPlanDetailsService";

interface RateBreakdownWithDialogProps {
  rate?: number;
  rateType?: RateType;
  rateModifier?: number;
}

const RATE_TYPE_LABELS: Record<RateType, string> = {
  [RateType.VALUE]: "Exact Value",
  [RateType.MULTIPLE_OF_COST]: "Multiple of Cost",
  [RateType.ADD_TO_COST]: "Add to Cost",
};

const RateBreakdownWithDialog: React.FC<RateBreakdownWithDialogProps> = ({
  rate,
  rateType,
  rateModifier,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showTimeout, setShowTimeout] = useState<NodeJS.Timeout | null>(null);

  const hasAdditionalInfo =
    Boolean(rateType) ||
    (rateModifier !== undefined && rateModifier !== null);

  const formatValue = (value: number) => {
    if (value === 0) return "0";
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    });
  };

  const handleMouseEnter = () => {
    if (!hasAdditionalInfo) return;

    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }

    const timeout = setTimeout(() => {
      setOpen(true);
      setShowTimeout(null);
    }, 200);
    setShowTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }

    const timeout = setTimeout(() => {
      setOpen(false);
    }, 300);
    setHoverTimeout(timeout);
  };

  const handlePopperMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
  };

  const handlePopperMouseLeave = () => {
    setOpen(false);
  };

  const handleClose = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
    setOpen(false);
  };

  const handleClickAway = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
    setOpen(false);
  };

  const triggerLabel = rate === undefined || rate === null ? "-" : formatValue(rate);

  return (
    <>
      <Box
        ref={anchorRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "relative",
          display: "inline-block",
          cursor: hasAdditionalInfo ? "pointer" : "default",
          color: COLORS.typographyColor,
          textDecoration: hasAdditionalInfo ? "underline" : "none",
          textDecorationStyle: hasAdditionalInfo ? "dotted" : "none",
          textUnderlineOffset: hasAdditionalInfo ? "3px" : "0",
        }}
      >
        <Typography variant="body2">{triggerLabel}</Typography>
      </Box>

      <Popper
        open={open && hasAdditionalInfo}
        anchorEl={anchorRef.current}
        placement="top-start"
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            onMouseEnter={handlePopperMouseEnter}
            onMouseLeave={handlePopperMouseLeave}
            sx={{
              p: 2,
              minWidth: 260,
              maxWidth: 380,
              boxShadow: 3,
              border: "1px solid #ccc",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Rate Breakdown
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  p: 0.5,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {rateType && (
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rate Type:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {RATE_TYPE_LABELS[rateType] ?? rateType}
                  </Typography>
                </Box>
              )}

              {rateModifier !== undefined && rateModifier !== null && (
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rate Modifier:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatValue(rateModifier)}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 0.5 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Rate:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {triggerLabel}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default RateBreakdownWithDialog;
